import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HashLink } from "react-router-hash-link";

import { Button, Card, Col, Row, Table, Typography, Select, message } from "antd";
import {
  DeleteOutlined,
  ShoppingOutlined,
  MinusOutlined,
  PlusOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";

import styled from "styled-components";

import useAuth from "../../auth";
import { useCart } from "../Cart/CartContext";

import { useMutation, useQuery } from "@apollo/client";
import { GET_APPLICATION_PANEL_DATA } from "../../graphql/queries";
import { NEW_ORDER_MUTATION } from "../../graphql/mutations";

import { Loading } from "components/Loading";

const { Title } = Typography;
const { Option } = Select;

const CartDetails = () => {
  const [cart, setCart] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);

  const { updateCartCount } = useCart();

  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const navigate = useNavigate();

  const { loading: applicationPanelDataLoading, data: applicationPanelData } = useQuery(
    GET_APPLICATION_PANEL_DATA,
    {},
  );

  const { isAuthenticated } = useAuth();

  const [errorMessage, setErrorMessage] = useState(null);
  const errorMessageRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);

  const [newOrderMutation] = useMutation(NEW_ORDER_MUTATION, {
    onCompleted: (data) => {
      if (data.newOrder.success) {
        setErrorMessage(null);
        localStorage.setItem("orderID", data.newOrder.orderId);
        navigate("/checkout");
      } else {
        setErrorMessage(data.newOrder.errors[0]);
        scrollToError();
        setSubmitting(false);
      }
    },
  });

  const scrollToError = () => {
    if (errorMessageRef.current) {
      errorMessageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // Helper to generate unique key per cart item combining product id + selected option ID
  const generateItemKey = (item) => {
    if (!item.selectedOption) return `${item.id}`;
    return `${item.id}_${item.selectedOption.id}`;
  };

  // Helper to get available quantity of selected option for an item
  const getAvailableQuantity = (item) => {
    if (!item.selectedOption) return 0;
    const matchingOption = item.options.find((opt) => opt.id === item.selectedOption.id);
    return matchingOption ? matchingOption.availableQuantity : 0;
  };

  useEffect(() => {
    const savedCart = localStorage.getItem("cart");
    if (savedCart) {
      const parsedCart = JSON.parse(savedCart);

      // Add itemKey to each cart item
      const cartWithKeys = parsedCart.map((item) => ({
        ...item,
        itemKey: generateItemKey(item),
      }));

      setCart(cartWithKeys);
      updateTotalPrice(cartWithKeys);
    }
  }, []);

  // Update the total price based on cart items
  const updateTotalPrice = (cartItems) => {
    const total = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    setTotalPrice(total);
  };

  // Handle counter change by itemKey with stock limit
  const handleCounterChange = (itemKey, change) => {
    const updatedCart = [...cart];
    const itemIndex = updatedCart.findIndex((item) => item.itemKey === itemKey);
    if (itemIndex === -1) return;

    const item = updatedCart[itemIndex];
    const currentQty = item.quantity;
    const availableQty = getAvailableQuantity(item);

    const newQty = Math.min(Math.max(1, currentQty + change), availableQty);

    if (change > 0 && currentQty === availableQty) {
      message.warning(t("cart.notEnoughStock"));
      return;
    }

    item.quantity = newQty;

    setCart(updatedCart);
    updateTotalPrice(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    updateCartCount();
  };

  // Handle removing an item by itemKey
  const handleRemoveItem = (itemKey) => {
    const updatedCart = cart.filter((item) => item.itemKey !== itemKey);
    setCart(updatedCart);
    updateTotalPrice(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    updateCartCount();
  };

  const clearCart = () => {
    setCart([]);
    setTotalPrice(0);
    localStorage.removeItem("cart");
    updateCartCount();
  };

  // Handle option change inside cart
  const handleOptionChange = (itemKey, newOptionValue) => {
    const updatedCart = [...cart];
    const itemIndex = updatedCart.findIndex((item) => item.itemKey === itemKey);
    if (itemIndex === -1) return;

    const item = updatedCart[itemIndex];

    // Find the option object matching the new value (for the single option category)
    const optionObj = item.options.find((opt) =>
      currentLanguage === "en" ? opt.valueEn === newOptionValue : opt.valueAr === newOptionValue,
    );

    if (!optionObj) return;

    // Update selectedOption with new option
    const newSelectedOption = optionObj;

    // Generate new itemKey based on new option
    const newItemKey = generateItemKey({ ...item, selectedOption: newSelectedOption });

    // Update the cart item with new option, new itemKey, and reset quantity to 1
    updatedCart[itemIndex] = {
      ...item,
      selectedOption: newSelectedOption,
      itemKey: newItemKey,
      quantity: 1,
    };

    setCart(updatedCart);
    updateTotalPrice(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    updateCartCount();
  };

  // Columns for the table, simplified for single option category per product
  const columns = [
    {
      dataIndex: "image",
      key: "image",
      render: (text, obj) => {
        const name = obj["name" + (currentLanguage === "en" ? "En" : "Ar")];
        return <img src={obj.picture} alt={name} style={{ width: 100, height: 100 }} />;
      },
    },
    {
      dataIndex: "name",
      key: "name",
      render: (text, obj) => obj["name" + (currentLanguage === "en" ? "En" : "Ar")],
    },
    {
      key: "option",
      title: t("cart.selectedOption"),
      render: (text, obj) => {
        if (!obj.selectedOption) return null;

        // Get option category info
        const categoryNameObj = obj.options[0]?.optionCategory;
        const categoryName =
          currentLanguage === "en" ? categoryNameObj?.nameEn : categoryNameObj?.nameAr || "";

        // All options for this category
        const optionObjs = obj.options;

        // Unique options by value
        const uniqueOptionObjs = [
          ...new Map(
            optionObjs.map((opt) => [currentLanguage === "en" ? opt.valueEn : opt.valueAr, opt]),
          ).values(),
        ];

        // Get current selected option value
        const selectedValue =
          currentLanguage === "en" ? obj.selectedOption.valueEn : obj.selectedOption.valueAr;

        return (
          <div>
            <div style={{ fontWeight: "bold", marginBottom: 4 }}>{categoryName}:</div>
            <Select
              style={{ width: "100%" }}
              value={selectedValue}
              onChange={(value) => handleOptionChange(obj.itemKey, value)}
            >
              {uniqueOptionObjs.map((opt) => {
                const value = currentLanguage === "en" ? opt.valueEn : opt.valueAr;
                return (
                  <Option key={value} value={value}>
                    {value}
                  </Option>
                );
              })}
            </Select>
          </div>
        );
      },
    },
    {
      dataIndex: "price",
      key: "price",
      render: (text) => (
        <span style={{ fontFamily: '"Figtree", sans-serif' }}>{text.toFixed(2) + " KD"}</span>
      ),
    },
    {
      dataIndex: "quantity",
      key: "quantity",
      render: (text, obj) => {
        const availableQty = getAvailableQuantity(obj);
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <StyledButton
              onClick={() => handleCounterChange(obj.itemKey, -1)}
              disabled={text === 1}
              icon={<MinusOutlined />}
            />
            <span style={{ margin: "0 10px", fontFamily: '"Figtree", sans-serif' }}>{text}</span>
            <StyledButton
              onClick={() => handleCounterChange(obj.itemKey, 1)}
              icon={<PlusOutlined />}
              disabled={text >= availableQty}
            />
          </div>
        );
      },
    },
    {
      dataIndex: "total",
      key: "total",
      render: (text, obj) => (
        <span style={{ fontFamily: '"Figtree", sans-serif' }}>
          {(obj.price * obj.quantity).toFixed(2) + " KD"}
        </span>
      ),
    },
    {
      key: "action",
      render: (text, obj) => (
        <StyledButton
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveItem(obj.itemKey)}
          danger
        />
      ),
    },
  ];

  const handleProceedToPayment = () => {
    setSubmitting(true);

    const data = cart.map((obj) => ({
      productId: parseInt(obj.id),
      quantity: obj.quantity,
      optionId: parseInt(obj.selectedOption?.id),
    }));

    newOrderMutation({
      variables: { productsList: data },
    });
  };

  if (applicationPanelDataLoading) return <Loading />;

  return (
    <Row gutter={24}>
      <Col xs={24} lg={16}>
        <Title level={2}>{t("cart.title")}</Title>
        <Table
          rowKey="itemKey"
          columns={columns}
          dataSource={cart}
          pagination={false}
          showHeader={false}
          scroll={{ x: true }}
        />
        <Row gutter={10} style={{ marginTop: 20 }}>
          <Col>
            <HashLink to="/#products">
              <Button type="link" icon={<ShoppingOutlined />} style={{ padding: "4px" }}>
                {t("cart.continueShoppingButton")}
              </Button>
            </HashLink>
          </Col>

          <Col>
            <Button
              type="link"
              danger
              onClick={() => clearCart()}
              icon={<DeleteOutlined />}
              style={{ padding: "4px" }}
            >
              {t("cart.clearCartButton")}
            </Button>
          </Col>
        </Row>
      </Col>

      <Col xs={24} lg={8} className="mt-4 mt-lg-0">
        <Card>
          <div ref={errorMessageRef}></div> {/* Error message ref */}
          {errorMessage && <p className="text-danger english-field">{errorMessage}</p>}
          <p>
            <span className="fw-bold">{t("cart.totalPrice")}:&nbsp;</span>
            <span style={{ fontFamily: '"Figtree", sans-serif' }}>{totalPrice.toFixed(2)} KD</span>
          </p>
          <Button
            type="primary"
            block
            onClick={() => handleProceedToPayment()}
            loading={submitting}
            disabled={
              !isAuthenticated ||
              cart.length === 0 ||
              totalPrice === 0 ||
              submitting ||
              !applicationPanelData?.applicationPanelData?.onlinePayments
            }
          >
            {t("cart.proceedToPaymentButton")}
          </Button>
          {!isAuthenticated && (
            <p
              style={{ color: "#ff4d4f", marginTop: "4px", display: "flex", alignItems: "center" }}
            >
              <ExclamationCircleOutlined
                style={currentLanguage === "en" ? { marginRight: "4px" } : { marginLeft: "4px" }}
              />
              {t("cart.loginToProceedMessage")}
            </p>
          )}
        </Card>
      </Col>
    </Row>
  );
};

export default CartDetails;

const StyledButton = styled(Button)`
  svg {
    margin: 0;
  }
`;
